/**
 * v1.3.16 — Connectivity source of truth.
 *
 * Single module that combines two independent signals into one
 * "effective online" state used by every queue-vs-live decision in
 * the app:
 *
 *   effectiveOnline = navigator.onLine && !manualAirplaneMode
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Before v1.3.16 the app read `navigator.onLine` directly from ~20
 * scattered call sites. That mode (fully-online / true-offline)
 * still works identically because when the manual toggle is OFF
 * (its default) `effectiveOnline === navigator.onLine`. This
 * module simply adds a *second* reason the app might behave as
 * offline: a user-initiated "Airplane Mode" toggle that queues
 * network + AI work locally even while the device is on the
 * network.
 *
 * SAFETY INVARIANTS
 * -----------------
 * 1. `manualAirplaneMode` can ONLY be flipped by an explicit user
 *    action (the header toggle). No automated code path is
 *    allowed to write it — a grep tripwire enforces this.
 * 2. The feature is gated behind
 *    `REACT_APP_ENABLE_MANUAL_AIRPLANE_MODE`. When the flag is
 *    off (or undefined), `setManualAirplaneMode` is a no-op and
 *    `getManualAirplaneMode` always returns `false`, so the
 *    combined signal degenerates to `navigator.onLine` — byte-
 *    for-byte identical to pre-v1.3.16 behavior.
 * 3. State persists in localStorage under a single key so an app
 *    refresh in airplane mode stays in airplane mode.
 * 4. Subscribers are notified on BOTH network changes AND toggle
 *    changes via a single unified `subscribeConnectivity` API.
 */
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'fmiq:manualAirplaneMode';
const EVT_NAME = 'fmiq:manual-airplane-change';

/**
 * Feature-flag guard. Read once at module load; the flag never
 * changes at runtime. When false, the whole manual-mode surface
 * is inert — toggle is a no-op, getters return false, subscribers
 * only fire for real network events.
 *
 * Note: `process.env.REACT_APP_*` is replaced literally at build
 * time by CRA's webpack DefinePlugin, so this file just reads it
 * directly. We DON'T wrap in `typeof process !== 'undefined'`
 * because `process` itself is NOT shimmed in the browser bundle
 * (only `process.env.REACT_APP_*` literals are inlined).
 */
export const MANUAL_AIRPLANE_ENABLED =
  process.env.REACT_APP_ENABLE_MANUAL_AIRPLANE_MODE === 'true';

/** True when the user has manually toggled airplane mode ON. */
export function getManualAirplaneMode() {
  if (!MANUAL_AIRPLANE_ENABLED) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * Set the manual airplane toggle. This is the ONLY write path;
 * do not persist to STORAGE_KEY from anywhere else.
 *
 * @param {boolean} enabled
 */
export function setManualAirplaneMode(enabled) {
  if (!MANUAL_AIRPLANE_ENABLED) return;
  const value = !!enabled;
  try {
    if (value) {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (_) {
    /* localStorage disabled (private mode) — swallow so the app
       still runs; the toggle just won't persist across refresh. */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVT_NAME, { detail: { manualAirplane: value } }));
  } catch (_) { /* older browsers */ }
}

/**
 * Synchronous "should the app treat itself as online right now?"
 * check. Use this in imperative code paths (services, sync
 * managers, one-off decisions inside handlers) that don't have a
 * React re-render lifecycle. In React components prefer the
 * `useConnectivity()` hook so the UI reacts.
 */
export function isEffectivelyOnline() {
  const net = (typeof navigator !== 'undefined') ? navigator.onLine !== false : true;
  return net && !getManualAirplaneMode();
}

/**
 * Subscribe to any change in the effective-online state — fires
 * on both browser `online`/`offline` events AND on manual toggle
 * changes. Returns an unsubscribe function.
 *
 * @param {(state: {effectiveOnline:boolean, networkOnline:boolean, manualAirplane:boolean}) => void} cb
 * @returns {() => void}
 */
export function subscribeConnectivity(cb) {
  const emit = () => {
    const networkOnline = (typeof navigator !== 'undefined') ? navigator.onLine !== false : true;
    const manualAirplane = getManualAirplaneMode();
    cb({
      networkOnline,
      manualAirplane,
      effectiveOnline: networkOnline && !manualAirplane,
    });
  };
  const onManual = () => emit();
  const onOnline = () => emit();
  const onOffline = () => emit();
  window.addEventListener(EVT_NAME, onManual);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  return () => {
    window.removeEventListener(EVT_NAME, onManual);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

/**
 * React hook variant. Provides the composed state + a stable
 * setter for the manual toggle.
 *
 * Callers that only need "am I effectively online?" can destructure
 * the boolean and ignore the rest.
 */
export function useConnectivity() {
  const [state, setState] = useState(() => ({
    networkOnline: (typeof navigator !== 'undefined') ? navigator.onLine !== false : true,
    manualAirplane: getManualAirplaneMode(),
    effectiveOnline: isEffectivelyOnline(),
  }));

  useEffect(() => subscribeConnectivity(setState), []);

  const setManual = useCallback((v) => setManualAirplaneMode(v), []);

  return {
    ...state,
    setManualAirplaneMode: setManual,
    featureEnabled: MANUAL_AIRPLANE_ENABLED,
  };
}
